import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { PRICING_COLUMN_TYPES } from '../../../../constants/FieldPricingTableColumnTypes';
import {
  getValueType,
  parseDisplayValue,
  formatCellValue,
  isFormattedCorrectly,
  calculateRowSubtotal
} from './pricingHelpers';
import PricingTableFieldDescriptionCell from './PricingTableFieldDescriptionCell';

const TableCell = styled.td`
  padding: 0;
  margin: 0;
  position: relative;
  text-align: right;
  height: 100%;
`;

const InputWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
`;

const StyledCellInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: ${(props) => (props.padding ? `${props.padding}px` : '0 8px 0 0')};
  margin: 0;
  border: none;
  box-sizing: border-box;
  border-radius: ${(props) => (props.borderRadius ? `${props.borderRadius}px` : '0')};
  text-align: ${(props) => props.textAlign || 'right'};
  font-size: ${(props) => (props.fontSize ? `${props.fontSize}px` : 'inherit')};
  color: ${(props) => props.fontColor || 'inherit'};
  font-style: ${(props) => props.fontStyle || 'normal'};
  font-weight: ${(props) => props.fontWeight || 'normal'};
  background-color: ${(props) => {
    if (props.isMuted) {
      return 'rgb(240, 240, 240)';
    } else if (props.backgroundColor) {
      return props.backgroundColor;
    }
    return 'transparent';
  }};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'text')};
`;

/**
 * IMPORTANT
 * 1. Any weirdness you see around the handleBlur or handleChange/CellChange is around TipTapEditor
 *    in the description cell not being an actual "input" and tiptap having their own internal rules
 *    around blur, focus, and logic.
 * 2. The current fix is to solve that when the user interacts with the tiptap editor context menu
 *    why it forces a blur event on the cell. Even though tiptap is not supposed to do that its should
 *    preserve the focus state like it does in our new RichTextField when its within a table it breaks it.
 */
const PricingTableFieldCell = ({
  cell,
  column,
  columnIndex,
  rowIndex,
  onCellChange,
  allColumns,
  mode,
  isEditable,
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textTransform,
  textDecoration,
  backgroundColor,
  borderColor,
  borderWidth,
  borderRadius,
  padding
}) => {
  const valueType = getValueType(column);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(parseDisplayValue(cell.value, valueType));
  const isSubtotalCell = column.type === PRICING_COLUMN_TYPES.SUBTOTAL;
  const isDescriptionCell = column.type === PRICING_COLUMN_TYPES.DESCRIPTION;

  // Make all cells editable regardless of mode, except for subtotal cells
  // and respect the overall isEditable flag
  const cellIsEditable = isEditable && !isSubtotalCell;

  // Ensure the cell value is properly formatted when the input is not being edited
  // Only for non-subtotal cells
  useEffect(() => {
    if (!isFocused && !isSubtotalCell && !isDescriptionCell && cell.value) {
      // Format the cell value if it's not already formatted correctly
      if (!isFormattedCorrectly(cell.value, valueType)) {
        const formattedValue = formatCellValue(cell.value, valueType);
        onCellChange(columnIndex, rowIndex, formattedValue);
      }
    }
  }, [cell.value, valueType, column.format, columnIndex, rowIndex, onCellChange, isSubtotalCell, isFocused]);

  useEffect(() => {
    if (!isFocused) {
      setInputValue(parseDisplayValue(cell.value, valueType));
    }
  }, [cell.value, valueType, column.format, isFocused]);

  const handleFocus = () => {
    // When focusing, set the input value to the parsed raw value
    if (cellIsEditable) {
      setInputValue(parseDisplayValue(cell.value, valueType));
      setIsFocused(true);
    }
  };

  const handleChange = (val) => {
    if (cellIsEditable) {
      setInputValue(val);
    }
  };

  const handleBlur = (isRte) => {
    setIsFocused(false);
    if (cellIsEditable) {
      const formattedValue = isRte ? inputValue : formatCellValue(inputValue, valueType);
      onCellChange(columnIndex, rowIndex, formattedValue);
    }
  };

  // For normal cells, just format the display value
  // For subtotal cells, always calculate on the fly
  let displayValue = inputValue;

  if (isSubtotalCell) {
    // Always calculate subtotals on the fly to ensure accuracy
    const subtotal = calculateRowSubtotal(allColumns, rowIndex);
    displayValue = formatCellValue(subtotal, valueType);
  } else {
    // For non-subtotal cells, use the regular display logic
    const formattedVal = isFormattedCorrectly(cell.value, valueType) ?
      cell.value :
      formatCellValue(cell.value, valueType);
    displayValue = isFocused ? inputValue : formattedVal;
  }

  const textAlign = isDescriptionCell ? 'left' : 'right';

  return (
    <TableCell>
      {isDescriptionCell ? (
        <PricingTableFieldDescriptionCell
          key={`desc-${column._id}-${rowIndex}`}
          cell={cell}
          columnIndex={columnIndex}
          columnId={column._id}
          rowIndex={rowIndex}
          onCellChange={handleChange}
          onCellBlur={handleBlur}
          mode={mode}
          isEditable={cellIsEditable}
          fontSize={fontSize}
          fontColor={fontColor}
          fontStyle={fontStyle}
          fontWeight={fontWeight}
          textAlign={textAlign}
          textTransform={textTransform}
          textDecoration={textDecoration}
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          borderWidth={borderWidth}
          borderRadius={borderRadius}
          padding={padding}
        />
      ) : (
        <InputWrapper>
          <StyledCellInput
            type="text"
            value={displayValue}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={handleFocus}
            onBlur={() => handleBlur()}
            disabled={!cellIsEditable}
            isMuted={isSubtotalCell}
            fontSize={fontSize}
            fontColor={fontColor}
            fontStyle={fontStyle}
            fontWeight={fontWeight}
            textAlign={textAlign}
            backgroundColor={backgroundColor}
            padding={padding}
          />
        </InputWrapper>
      )}
    </TableCell>
  );
};

export default PricingTableFieldCell;

PricingTableFieldCell.propTypes = {
  cell: PropTypes.shape({
    _id: PropTypes.string,
    value: PropTypes.any
  }),
  column: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    format: PropTypes.string,
    hidden: PropTypes.bool
  }).isRequired,
  columnIndex: PropTypes.number.isRequired,
  rowIndex: PropTypes.number.isRequired,
  onCellChange: PropTypes.func.isRequired,
  allColumns: PropTypes.array,
  mode: PropTypes.string,
  isEditable: PropTypes.bool,
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fontColor: PropTypes.string,
  fontStyle: PropTypes.string,
  fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  textTransform: PropTypes.string,
  textDecoration: PropTypes.string,
  backgroundColor: PropTypes.string,
  borderColor: PropTypes.string,
  borderWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
